/**
 * @module
 *
 * @import {InternalEngine, Controller} from '@acng/frontend-stargazer';
 */

export {CLICK_POPUP};

/* prettier-ignore */
import {AttributeLink, CONNECT, Engine, getConfig, getNodes, resolve} from '@acng/frontend-stargazer';
import {close, open} from '@acng/frontend-relativity';
import {popup} from '@acng/frontend-discovery';
import {create, DIV} from '@acng/frontend-bounty/dom/element.js';
import {guard, IS, OPTIONAL, STRING, TUPLE} from '@acng/frontend-rubicon';

import {onClick} from '../service/worker.js';
import {enter, leave} from '../service/animate-class.js';
import {replaceAll} from '@acng/frontend-bounty/dom/move.js';
import {extend} from '@acng/frontend-bounty/object.js';
import {WeakStore} from '@acng/frontend-bounty/collection.js';

/**
 * @group DOM Attribute
 */
const CLICK_POPUP = 'click-popup';

/**
 * @implements {Controller}
 */
class ClickPopupController {
  #path;
  /**
   * @type {(key: Element) => HTMLDivElement}
   */
  static #children = WeakStore(() => create(DIV));

  /**
   * @param {string} path
   */
  constructor(path, lightDismiss = 5000) {
    this.#path = path;
    this.lightDismiss = lightDismiss
  }

  /**
   * @param {Element} element
   * @param {Node} node
   * @param {InternalEngine} engine
   */
  [CONNECT](element, node, engine) {
    return onClick(element, () => this.#open(element, engine));
  }

  /**
   * @param {Element} element
   * @param {InternalEngine} engine
   */
  async #open(element, engine) {
    const controller = popup(element);
    const div = ClickPopupController.#children(element);

    if (controller.isOpen(div)) {
      return;
    }

    const linker = await getEngine(engine, this.#path);

    open(element, div);
    const scope = extend(getConfig(engine), {
      /**
       * @type {import('@acng/frontend-stargazer').Controller}
       */
      close: {
        [CONNECT](element) {
          onClick(element, () => controller.close(div));
        },
      },
    });
    linker.toElement(div, scope);
    controller
      .add(div, this.lightDismiss, enter, leave)
      .finally(() => {
        linker.disconnect();
        close(element, div);
        replaceAll(div);
      });
  }
}

AttributeLink(CLICK_POPUP, (element, value, engine) => {
  const ctrl = new ClickPopupController(value);

  return () => ctrl[CONNECT](element, element, engine);
});

  const syntax = /^(#)?(.*)$/;

  /**
   * @param {InternalEngine} engine
   * @param {string} value
   * @returns {Promise<Engine>}
   */
  export const getEngine = async (engine, value) => {
    const match = syntax.exec(value);
    ASSERT: guard(match, TUPLE(STRING, OPTIONAL(STRING), STRING), `[$name] syntax`);
    const [, isLocal, alias] = match;

    if (isLocal) {
      const {[alias]: template} = getNodes(engine);
      ASSERT: guard(template, IS(HTMLTemplateElement));
      const linker = new Engine(template);
      await linker.lookup();

      return linker;
    } else {
      return Engine.root.render(...resolve(alias));
    }
  };

export {ClickPopupController};
